<!-- HomePage.vue -->
<script setup>
import { computed, defineProps, onMounted, ref, toRefs } from 'vue'
import { RouterLink } from 'vue-router'
import {
  AlertTriangle,
  Calendar,
  ChartLine,
  ChevronRight,
  Clipboard,
  Clock,
  MapPin,
  Menu,
  Search,
  Settings,
  UserPlus,
  Video,
} from 'lucide-vue-next'
import { useRouter } from 'vue-router' // Import conversation store
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import HomeActionBar from '@/components/HomeActionBar.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Empty from '@/components/Empty.vue'

import { formatDayDate, formatTime } from '@/lib/utils/dateUtils'
import HomeConversationSection from '@/components/HomeConversationSection.vue'

import trpc from '@/trpc'
import { useMeetingStore } from '@/store/meetingStore' // Import the meeting store
import { useConversationStore } from '@/store/conversationStore'

// Define props with default values
const props = defineProps({
  session: {
    type: Object,
    default: () => ({}),
  },
})

// Get the stores
const meetingStore = useMeetingStore()
const conversationStore = useConversationStore()

const { session } = toRefs(props)
const user = computed(() => ({
  email: session.value?.user?.email || '',
  avatar: session.value?.user?.user_metadata?.avatar_url || '',
  name:
    session.value?.user?.user_metadata?.full_name
    || session.value?.user?.email?.split('@')[0]
    || '',
}))

// Get clients from the store instead of props
const clients = computed(() => meetingStore.patients)

// Search functionality
const searchQuery = ref('')
const filteredClients = computed(() => {
  if (!searchQuery.value.trim()) {
    return clients.value
  }
  const query = searchQuery.value.toLowerCase()
  return clients.value.filter(client =>
    client.name.toLowerCase().includes(query),
  )
})

const clientsCount = computed(() => clients.value.length || 0)

// Add constant for maximum clients to show and ref for showing all clients
const MAX_CLIENTS_TO_SHOW = 5
const showAllClients = ref(false)

// Add computed property for displayed clients
const displayedClients = computed(() => {
  if (showAllClients.value || searchQuery.value.trim()) {
    return filteredClients.value
  }
  return filteredClients.value.slice(0, MAX_CLIENTS_TO_SHOW)
})

// Toggle function to show all clients
function toggleShowAllClients() {
  showAllClients.value = !showAllClients.value
}

function hello() {
  /*
  trpc.hello.query("helllloooo999").then((res) => {
    console.log(res);
  });
  return "Hello World";
  */
}

function initializeMentor() {
  /// TBD: This is just a placeholder for now

  trpc.initializeMentor.query('helllloooo999').then((res) => {
    console.log(res)
  })
  return 'Hello World'
}

// Data for appointments - use computed to get from meetingStore
const appointments = computed(() => {
  if (!meetingStore.meets || meetingStore.meets.length === 0) {
    return []
  }

  const now = new Date()

  // Sort meetings by start time, filter out past meetings
  const sortedMeets = [...meetingStore.meets]
    .filter((meet) => {
      const meetingStart = new Date(meet.startTime)
      return meet.status === 'scheduled' && meetingStart >= now
    })
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
    .slice(0, 5) // Show the next 5 upcoming meetings

  return sortedMeets.map((meet) => {
    const meetDate = new Date(meet.startTime)
    const endTime = new Date(meetDate)
    endTime.setMinutes(endTime.getMinutes() + meet.duration)

    // Find patient for this meeting
    const patient = meetingStore.patients.find(p => p.id === meet.patientId)

    return {
      ...meet,
      id: meet.id,
      clientName: patient?.name || 'Client',
      timeSlot: `${formatTime(meetDate)} à ${formatTime(endTime)}`,
      day: formatDayDate(meetDate),
      date: meetDate.getDate(),
      friendlyWeekday: formatDayDate(meetDate),
    }
  })
})

const router = useRouter()

function clickMeeting(meetingId) {
  console.log('Meeting clicked:', meetingId)
  // Implementation for starting a meeting
  /// navugate
  router.push(`/meets/${meetingId}`)
}

// Data for tasks
const tasks = ref([
  {
    id: 1,
    clientName: 'Gabriel Beaulieu',
    description:
      'Dernières activités non complétées. Envoyer un message de motivation.',
    actionLabel: 'Relancer',
  },
  {
    id: 2,
    clientName: 'Erin Carlone',
    description:
      'Dernières activités non complétées. Envoyer un message de motivation.',
    actionLabel: 'Relancer',
  },
  {
    id: 3,
    clientName: 'Josiane Decarie',
    description: 'Prêt(e) pour une nouvelle activité',
    actionLabel: 'Envoyer',
  },
])

// Data for recommendations
const recommendations = ref([
  /*
  {
    id: 1,
    clientName: "Alexandre Lepage",
    description:
      "Notes de la dernière séance : Wiso vous a préparé un debriefing.",
  },
  {
    id: 2,
    clientName: "Souhaib Khadraoui",
    description:
      "A exprimé des difficultés à structurer son temps, proposez-lui une activité sur la gestion des priorités.",
  },
  {
    id: 3,
    clientName: "Julie Bernard",
    description: "Recommandation",
  }, */
])

// Metrics for dashboard cards
const metrics = computed(() => ({
  toPrepare: meetingStore.meets.filter(
    meet => meet.status === 'scheduled' && !meet.isPrepared,
  ).length,
  scheduled: meetingStore.meets.filter(meet => meet.status === 'scheduled')
    .length,
  activeClients: clientsCount.value,
}))

// Concepts data
const concepts = ref([
  {
    id: 1,
    title: 'Débuter avec Selfwise',
    description:
      'Découvrez comment Selfwise transforme l\'approche du coaching professionnel. Notre plateforme combine l\'intelligence artificielle et les meilleures pratiques du coaching pour offrir un accompagnement personnalisé et innovant. Idéal pour les coachs qui souhaitent digitaliser leur pratique et maximiser l\'impact de leurs séances.',
    category: 'introduction',
    link: '/getting-started',
  },
  {
    id: 2,
    title: 'Créer des activités personnalisées',
    description:
      'Développez des exercices et des programmes sur mesure pour vos clients. Notre interface intuitive vous permet de créer des parcours de coaching adaptés aux objectifs spécifiques de chaque client, qu\'il s\'agisse de développement professionnel, de leadership ou de gestion du changement.',
    category: 'création',
    link: '/create-activity',
  },
  {
    id: 3,
    title: 'Participez au développement',
    description:
      'Votre expertise enrichit notre plateforme ! En tant que coach professionnel, vos retours sont précieux pour façonner l\'avenir du coaching digital. Partagez vos idées pour améliorer Selfwise et contribuez à créer des outils qui répondent aux besoins réels des coachs et de leurs clients.',
    category: 'communauté',
    link: '/contribute',
  },
])

function handleNewActivity() {
  console.log('New activity action triggered')
  // Implementation for new activity
}

function handlePlanMeeting() {
  console.log('Plan meeting action triggered')
  // Implementation for planning a meeting
}

function handleAddClient() {
  console.log('Add client action triggered')
  // Implementation for adding a client
}

// Load data on mount
onMounted(() => {
  initializeMentor()

  // Only log data, no need to fetch again since App.vue already fetched it
  console.log('Using data from meeting store')
  console.log('Patients:', meetingStore.patients)
  console.log('Meetings:', meetingStore.meets)

  // If data isn't available yet, it will be reactive when it arrives
})

// Import date formatting utils instead of defining locally

// Check if data is loading
const isLoading = computed(
  () => meetingStore.patientsLoading || meetingStore.meetsLoading,
)

// Check if there's an error
const storeError = computed(() => meetingStore.error)

const todaysDate = computed(() => {
  return formatDayDate(new Date())
})
</script>

<template>
  <div class="bg-white">
    <div class="container">
      <PageHeadingBar title=" Tableau de bord " :info="infoTooltip">
        <!--
           <Button class="w-full mt-4" variant="" to="/invite-client">
          <UserPlus class="mr-2 h-4 w-4" />
          Ajouter un client
        </Button> -->

        <ElipsisMenu :items="[]" hori info="gfgf" title="R s" />
      </PageHeadingBar>
    </div>
  </div>
  <div class="bg-crazy img4">
    <!-- Action Bar -->
    <HomeActionBar
      style="max-width: 2070px"
      @new-activity="handleNewActivity"
      @plan-meeting="handlePlanMeeting"
      @add-client="handleAddClient"
    />

    <div class="min-h-screen p-6 container">
      <div style="max-width: 2000px">
        <!-- Header -->
        <header class="flex justify-between items-center mb-8">
          <h1 class="text-4xl font-bold">
            Bonjour, {{ String(user.name).split(" ")[0] }}
            <div class="block text-lg font-normal">
              {{ todaysDate }} | Continuons à révéler le potentiel de vos
              clients.
            </div>
          </h1>

          <div class="flex gap-2 hidden">
            <Button variant="ghost" size="icon" class="bg-black text-white">
              <Menu />
            </Button>
            <Button variant="outline" size="icon">
              <Settings />
            </Button>
          </div>
        </header>

        <!-- Loading indicator -->
        <div v-if="isLoading" class="text-center py-4">
          <p class="text-gray-600">
            Chargement des données...
          </p>
        </div>

        <!-- Error message -->
        <div
          v-else-if="storeError"
          class="bg-red-100 text-red-700 p-4 rounded-lg mb-6"
        >
          <p>{{ storeError }}</p>
        </div>

        <div v-else class="flex flex-col md:flex-row gap-6">
          <!-- Main Content Column (Left) -->
          <div class="w-full md:w-2/3">
            <DashboardCards
              class="mb-8"
              :to-prepare="metrics.toPrepare"
              :scheduled="metrics.scheduled"
              :active-clients="metrics.activeClients"
            />

            <!-- Appointments Section -->

            <section class="mb-8">
              <Card>
                <div class="flex">
                  <div class="flex-grow">
                    <span class="text-xl font-bold p-4 block">Prochains rendez-vous
                    </span>
                  </div>
                  <RouterLink to="/meet" class="block mt-4">
                    <Button variant="text" class="mt-4">
                      Voir tous les rendez-vous
                    </Button>
                  </RouterLink>
                </div>

                <!-- Appointment Cards -->
                <div v-if="appointments.length > 0" class="space-y-4">
                  <div
                    v-for="appointment in appointments"
                    :key="appointment.id"
                    class="bg-white rounded-lg p-4 shadow-sm"
                  >
                    <div class="flex justify-between items-start">
                      <div class="flex gap-4">
                        <div class="bg-purple-100 p-3 rounded-full mr-3">
                          <Calendar class="text-purple-600 h-6 w-6" />
                        </div>

                        <div>
                          <h3 class="font-bold text-lg">
                            {{ appointment.clientName }}
                            <Badge class="ml-2 bg-blue-100 text-blue-800">
                              planifié***
                            </Badge>
                          </h3>

                          <div
                            class="flex items-center text-gray-500 gap-1 mt-1 text-sm"
                          >
                            <Calendar class="h-4 w-4" />
                            <span>{{ appointment.friendlyWeekday }}</span>
                            <Clock class="h-4 w-4 ml-2" />
                            <span>{{ appointment.timeSlot }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <RouterLink :to="`/meets/${appointment.id}`">
                          <Button variant="outline">
                            Préparer la séance
                          </Button>
                        </RouterLink>
                        <RouterLink :to="`/meet/${appointment.patientId}`">
                          <Button>
                            <Video class="h-4 w-4 mr-2" />
                            Rejoindre
                          </Button>
                        </RouterLink>

                        <!--
                        <Button
                          variant="outline"
                          class="flex items-center gap-1"
                          @click="
                            conversationStore.startMeetingConversation(
                              appointment
                            )
                          "
                        >
                          <HelpCircle class="h-4 w-4" />
                          Wiso
                        </Button>
                        <Button variant="ghost" size="icon">
                          <MoreVertical />
                        </Button>
                        -->
                      </div>
                    </div>
                  </div>
                </div>

                <!-- No appointments message -->
                <div v-else class="bg-white rounded-lg p-4 shadow-sm">
                  <Empty
                    title="Aucun rendez-vous planifié"
                    description="Vous n'avez pas de rendez-vous planifiés pour le moment"
                    icon="calendar"
                    :primary-cta="{
                      label: 'Planifier un rendez-vous',
                      to: '/meet',
                    }"
                  />
                </div>
              </Card>
            </section>

            <!-- Conversations Section
            <veHomeConrsationSection  />
            -->

            <!-- Tasks Section -->
            <section class="mb-8">
              <Card>
                <div class="flex">
                  <div class="flex-grow">
                    <span class="text-xl font-bold p-4 block">Séances à préparer
                      <Badge class="ml-2 bg-blue-100 text-blue-800">
                        {{ metrics.toPrepare }} à préparer
                      </Badge>
                    </span>
                  </div>

                  <!--
                  <router-link :to="'/meet'" class="block mt-4">
                    <Button variant="text" class="mt-4">
                      Voir toutes les séances
                    </Button>
                  </router-link> -->
                </div>

                <div v-if="appointments.length > 0" class="space-y-4">
                  <div
                    v-for="appointment in appointments"
                    :key="appointment.id"
                    class="bg-white rounded-lg p-4 shadow-sm"
                  >
                    <div class="flex justify-between items-start">
                      <div class="flex gap-4">
                        <div class="bg-purple-100 p-3 rounded-full mr-3">
                          <Clipboard class="text-purple-600 h-6 w-6" />
                        </div>

                        <div>
                          <h3 class="font-bold text-lg">
                            {{ appointment.clientName }}
                          </h3>

                          <div
                            class="flex items-center text-gray-500 gap-1 mt-1 text-sm"
                          >
                            <Calendar class="h-4 w-4" />
                            <span>{{ appointment.friendlyWeekday }}</span>
                            <Clock class="h-4 w-4 ml-2" />
                            <span>{{ appointment.timeSlot }}</span>
                          </div>

                          <div class="text-green-500 text-sm mt-2">
                            <ChartLine class="h-4 w-4 inline mr-2" />
                            <span>Axe: à déterminer </span>
                          </div>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <RouterLink :to="`/client/${appointment.patientId}`">
                          <Button variant="outline">
                            Consulter le profil
                          </Button>
                        </RouterLink>

                        <RouterLink :to="`/meets/${appointment.id}`">
                          <Button variant="">
                            Préparer
                          </Button>
                        </RouterLink>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="bg-white rounded-lg p-4 shadow-sm">
                  <Empty
                    title="Aucune séance à préparer"
                    description="Vous n'avez aucune séance à préparer pour le moment"
                    icon="file"
                    :primary-cta="{ label: 'Planifier une séance', to: '/meet' }"
                  />
                </div>
              </Card>
            </section>

            <!-- Recommendations Section -->

            <HomeConversationSection />

            <!--
            <section class="mb-8 bg-white rounded-lg p-6 shadow-sm">
              <h2 class="text-xl font-bold mb-6">Aide intelligente</h2>

              <div class="space-y-6">
                <div
                  v-for="recommendation in recommendations"
                  :key="recommendation.id"
                  class="flex justify-between items-center"
                >
                  <div class="flex gap-4">
                    <div class="bg-violet-100 p-3 rounded-lg">
                      <Sparkles class="h-5 w-5 text-violet-600" />
                    </div>
                    <div>
                      <h3 class="font-medium">
                        {{ recommendation.clientName }}
                      </h3>
                      <p class="text-gray-600 text-sm">
                        {{ recommendation.description }}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon">
                    <ArrowRight />
                  </Button>
                </div>
              </div>
            </section> -->

            <!-- Concepts Section
            <section class="mb-8">
              <h2 class="text-xl font-bold mb-6">Concepts principaux</h2>
              <div class="grid gap-6">
                <Card
                  v-for="concept in concepts"
                  :key="concept.id"
                  class="p-6 transition-shadow hover:shadow-lg"
                >
                  <RouterLink :to="concept.link" class="block">
                    <h2 class="text-2xl font-semibold mb-2">
                      {{ concept.title }}
                    </h2>
                    <p class="text-muted-foreground">
                      {{ concept.description }}
                    </p>
                    <Badge class="mt-4">{{ concept.category }}</Badge>
                  </RouterLink>
                </Card>
              </div>
            </section> -->

            <DashboardEvo />
          </div>

          <!-- Sidebar Column (Right) -->
          <div class="w-full md:w-1/3">
            <!-- Next Meeting
             v-if="meetingStore.nextScheduledPatient"
             -->
            <section class="mb-8">
              <Card class="whibg-te rounded-lg shadow-lg p-6 mt-0">
                <h2 class="text-xl font-bold mb-6">
                  Prochains rendez-vous
                </h2>
                <DashboardCalendar />
              </Card>

              <!--
              <div class="bg-white rounded-lg p-4 shadow-sm">
                <div class="flex gap-4 mb-3">
                  <div class="bg-blue-100 p-2 rounded-full">
                    <Clock class="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 class="font-medium">
                      {{ meetingStore.nextScheduledPatient.name }}
                    </h3>
                    <p class="text-gray-600 text-sm">
                      {{ meetingStore.nextScheduledPatient.meetingTime }}
                    </p>
                    <p class="text-gray-600 text-sm">
                      {{ meetingStore.nextScheduledPatient.agenda }}
                    </p>
                  </div>
                </div>
                <div class="flex justify-end">
                  <Button> Préparer la séance </Button>
                </div>
              </div>
-->
            </section>

            <!-- Actions Section -->
            <section class="mb-8 hidden">
              <h2 class="text-xl font-bold mb-6">
                Actions à prendre
              </h2>

              <div class="space-y-6">
                <!-- Unconfirmed Appointment -->
                <div class="bg-white rounded-lg p-4 shadow-sm">
                  <div class="flex gap-4 mb-3">
                    <div class="bg-orange-100 p-2 rounded-full">
                      <AlertTriangle class="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <h3 class="font-medium">
                        Marc-André Bourassa
                      </h3>
                      <p class="text-gray-600 text-sm">
                        RDV non confirmé pour demain
                      </p>
                    </div>
                  </div>
                  <div class="flex justify-end">
                    <Button variant="outline">
                      Relancer
                    </Button>
                  </div>
                </div>

                <!-- New Appointment Request -->
                <div class="bg-white rounded-lg p-4 shadow-sm">
                  <div class="flex gap-4 mb-3">
                    <div class="bg-orange-100 p-2 rounded-full">
                      <AlertTriangle class="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <h3 class="font-medium">
                        Karine Gendreau
                      </h3>
                      <p class="text-gray-600 text-sm">
                        Nouvelle demande de rdv
                      </p>
                      <div class="mt-3 space-y-2 text-sm text-gray-500">
                        <div class="flex items-center gap-1">
                          <Calendar class="h-4 w-4" />
                          <span>Jeudi 27 mars 2025</span>
                        </div>
                        <div class="flex items-center gap-1">
                          <MapPin class="h-4 w-4" />
                          <span>En ligne</span>
                        </div>
                        <div class="flex items-center gap-1">
                          <Clock class="h-4 w-4" />
                          <span>15h00 à 15h45</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex justify-end gap-2">
                    <Button> Accepter </Button>
                    <Button variant="outline">
                      Refuser
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            <!-- Clients Section -->
            <section class="mb-8">
              <div class="bg-white rounded-lg p-4 shadow-sm">
                <div class="flex items-center justify-between mb-4">
                  <h2 class="flex-grow text-xl font-bold mb-6">
                    {{ clients.length }} clients
                    <!--
                <Badge class="ml-2 bg-green-100 text-green-800">
                  {{ meetingStore.availableRoomsCount }} disponibles
                </Badge> -->
                  </h2>
                  <Button class="mt-4" variant="" to="/invite-client">
                    <UserPlus class="mr-2 h-4 w-4" />
                    Ajouter un client
                  </Button>
                </div>
                <!-- Fixed Search Bar -->
                <div class="relative mb-4">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Search class="h-4 w-4 text-gray-400" />
                  </div>
                  <Input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Rechercher"
                    class="pl-10 w-full"
                  />
                </div>

                <!-- Clients List - Now using displayedClients instead of filteredClients -->
                <div v-if="displayedClients.length > 0" class="space-y-2">
                  <RouterLink
                    v-for="client in displayedClients"
                    :key="client.id"
                    :to="`/client/${client.id}`"
                    class="flex justify-between items-center p-2 hover:bg-gray-50 rounded"
                  >
                    <div>
                      <p class="font-medium">
                        {{ client.name }}
                      </p>
                      <p class="text-xs text-muted-foreground">
                        <!--
                        <Badge
                          :class="meetingStore.getBadgeClass(client.roomStatus)"
                        >
                          {{ meetingStore.formatStatus(client.roomStatus) }}
                        </Badge>
                        <span v-if="client.nextMeeting" class="ml-2">
                          {{ formatDate(client.nextMeeting) }}
                        </span>
                        -->
                      </p>
                    </div>
                    <Button variant="ghost" size="icon">
                      <ChevronRight class="h-4 w-4" />
                    </Button>
                  </RouterLink>
                </div>
                <div v-else class="text-center py-4">
                  <Empty
                    v-if="searchQuery"
                    title="Aucun résultat"
                    description="Aucun client ne correspond à votre recherche"
                    icon="user"
                  />
                  <Empty
                    v-else
                    title="Aucun client disponible"
                    description="Vous n'avez pas encore de clients"
                    icon="user"
                    :primary-cta="{
                      label: 'Ajouter un client',
                      to: '/invite-client',
                    }"
                  />
                </div>

                <!-- Show All Clients Toggle Button -->
                <div
                  v-if="
                    filteredClients.length > MAX_CLIENTS_TO_SHOW && !searchQuery
                  "
                  class="mt-4 text-center"
                >
                  <Button variant="ghost" @click="toggleShowAllClients">
                    {{ showAllClients ? "Voir moins" : "Voir tous..." }}
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-surface {
  background-color: hsl(var(--background));
}
</style>
